import { useState } from 'react';

import {
  Button,
  Card,
  Checkbox,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Input,
  Label,
  cn,
} from '@design-system';

import { Check, ChevronDown, Search, Trash2 } from 'lucide-react';

import type { API } from '@/api/types';

type DistributionPartnerSummary = API.DistributionPartnerSummary;
type DistributionContactWithPartner = API.DistributionPartnerContact & { partnerKey: string };

interface PartnerConnectionFormProps {
  partners: DistributionPartnerSummary[];
  isLoadingPartners: boolean;
  contacts: DistributionContactWithPartner[];
  isLoadingContacts: boolean;
  selectedPartnerKeys: string[];
  selectedContactIds: string[];
  onTogglePartner: (partnerKey: string) => void;
  onToggleContact: (advisorId: string, checked: boolean) => void;
  onToggleContacts: (advisorIds: string[], checked: boolean) => void;
  onConnect: () => void;
  isConnecting: boolean;
  onRequestOther: (partnerKey: string, name: string, email: string) => void;
  isRequestingOther: boolean;
  onRequestDisconnect: (contact: DistributionContactWithPartner) => void;
  disabled?: boolean;
  idPrefix?: string;
}

function contactDisplayName(contact: DistributionContactWithPartner): string {
  const name = `${contact.advisorFirstName ?? ''} ${contact.advisorLastName ?? ''}`.trim();

  return name || contact.advisorEmail;
}

// Once the partner list grows past a few, the logo grid gets unwieldy — switch to a searchable
// multi-select dropdown with logos instead.
const PARTNER_GRID_MAX = 3;

interface PartnerMultiSelectProps {
  partners: DistributionPartnerSummary[];
  selectedPartnerKeys: string[];
  onTogglePartner: (partnerKey: string) => void;
  disabled?: boolean;
}

function PartnerMultiSelect({
  partners,
  selectedPartnerKeys,
  onTogglePartner,
  disabled,
}: PartnerMultiSelectProps) {
  const selectedNames = partners
    .filter(partner => selectedPartnerKeys.includes(partner.partnerKey))
    .map(partner => partner.displayName);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className="truncate">
            {selectedNames.length > 0
              ? selectedNames.join(', ')
              : 'Select your distribution partners'}
          </span>
          <ChevronDown className="size-4 shrink-0 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-80 w-(--radix-dropdown-menu-trigger-width) overflow-y-auto"
      >
        {partners.map(partner => (
          <DropdownMenuCheckboxItem
            key={partner.partnerKey}
            checked={selectedPartnerKeys.includes(partner.partnerKey)}
            // Keep the menu open so several partners can be toggled in one pass.
            onSelect={event => event.preventDefault()}
            onCheckedChange={() => onTogglePartner(partner.partnerKey)}
            className="gap-2"
          >
            {partner.logoUrl && (
              <img
                src={partner.logoUrl}
                alt=""
                className="h-5 max-w-16 object-contain"
              />
            )}
            <span>{partner.displayName}</span>
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function PartnerConnectionForm({
  partners,
  isLoadingPartners,
  contacts,
  isLoadingContacts,
  selectedPartnerKeys,
  selectedContactIds,
  onTogglePartner,
  onToggleContact,
  onToggleContacts,
  onConnect,
  isConnecting,
  onRequestOther,
  isRequestingOther,
  onRequestDisconnect,
  disabled = false,
  idPrefix = '',
}: PartnerConnectionFormProps) {
  // Only enable "Connect" when a not-yet-connected contact is selected. Re-connecting contacts
  // that are already connected would re-run the connect/auto-share call for no change.
  const hasNewContactSelection = contacts.some(
    contact => selectedContactIds.includes(contact.advisorId) && !contact.isConnected,
  );
  const hasPartnerSelection = selectedPartnerKeys.length > 0;
  const selectedPartners = partners.filter(partner =>
    selectedPartnerKeys.includes(partner.partnerKey),
  );

  const [contactSearch, setContactSearch] = useState('');
  const normalizedSearch = contactSearch.trim().toLowerCase();
  const filteredContacts = normalizedSearch
    ? contacts.filter(contact =>
        `${contact.advisorFirstName ?? ''} ${contact.advisorLastName ?? ''} ${contact.advisorEmail}`
          .toLowerCase()
          .includes(normalizedSearch),
      )
    : contacts;

  const allFilteredSelected =
    filteredContacts.length > 0 &&
    filteredContacts.every(contact => selectedContactIds.includes(contact.advisorId));

  const [otherName, setOtherName] = useState('');
  const [otherEmail, setOtherEmail] = useState('');
  const [otherPartnerKey, setOtherPartnerKey] = useState('');
  const otherEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(otherEmail.trim());
  const effectiveOtherPartnerKey =
    otherPartnerKey && selectedPartnerKeys.includes(otherPartnerKey)
      ? otherPartnerKey
      : (selectedPartnerKeys[0] ?? '');

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <p className="text-foreground text-sm font-medium">
          Which distribution partners do you work with?
        </p>
        {isLoadingPartners && <p className="text-muted-foreground text-sm">Loading partners…</p>}
        {!isLoadingPartners && partners.length > PARTNER_GRID_MAX ? (
          <PartnerMultiSelect
            partners={partners}
            selectedPartnerKeys={selectedPartnerKeys}
            onTogglePartner={onTogglePartner}
            disabled={disabled}
          />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {partners.map(partner => {
              const selected = selectedPartnerKeys.includes(partner.partnerKey);
              return (
                <button
                  key={partner.partnerKey}
                  type="button"
                  disabled={disabled}
                  onClick={() => onTogglePartner(partner.partnerKey)}
                  className={cn(
                    'hover:bg-muted relative flex flex-col items-center justify-center gap-2 rounded-md border p-4 text-center text-base font-medium transition-colors',
                    { 'border-primary bg-primary/5': selected },
                  )}
                >
                  {selected && (
                    <span className="bg-primary absolute right-2 top-2 flex size-5 items-center justify-center rounded-full text-white">
                      <Check className="size-3" />
                    </span>
                  )}
                  {partner.logoUrl && (
                    <img
                      src={partner.logoUrl}
                      alt={`${partner.displayName} logo`}
                      className="h-10 max-w-32 object-contain"
                    />
                  )}
                  <span>{partner.displayName}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {hasPartnerSelection && (
        <div className="space-y-4">
          <p className="text-foreground text-sm font-medium">
            Which team members do you work with?
          </p>

          {isLoadingContacts && <p className="text-muted-foreground text-sm">Loading contacts…</p>}

          {!isLoadingContacts && contacts.length === 0 && (
            <p className="text-muted-foreground text-sm">
              No contacts are available for the selected partner(s) yet. Use the option below to
              request one.
            </p>
          )}

          {contacts.length > 0 && (
            <div className="space-y-3">
              <div className="relative">
                <Search className="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2" />
                <Input
                  value={contactSearch}
                  disabled={disabled}
                  placeholder="Search by name or email…"
                  onChange={event => setContactSearch(event.target.value)}
                  className="pl-10"
                />
              </div>

              {filteredContacts.length > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">
                    {selectedContactIds.length} selected
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={disabled}
                    onClick={() =>
                      onToggleContacts(
                        filteredContacts.map(contact => contact.advisorId),
                        !allFilteredSelected,
                      )
                    }
                  >
                    {allFilteredSelected ? 'Deselect all' : 'Select all'}
                  </Button>
                </div>
              )}

              <div className="max-h-120 space-y-2 overflow-y-auto rounded-xl border p-2">
                {filteredContacts.length === 0 ? (
                  <p className="text-muted-foreground py-6 text-center text-sm">
                    No team members match “{contactSearch}”.
                  </p>
                ) : (
                  filteredContacts.map(contact => {
                    const checked = selectedContactIds.includes(contact.advisorId);
                    return (
                      <div
                        key={contact.advisorId}
                        className={cn(
                          'hover:bg-muted flex items-start gap-2 rounded-md border p-2 transition-colors',
                          { 'border-primary bg-primary/5': checked },
                        )}
                      >
                        <Label
                          htmlFor={`${idPrefix}contact-${contact.advisorId}`}
                          className="flex flex-1 cursor-pointer items-start gap-3 p-2"
                        >
                          <Checkbox
                            id={`${idPrefix}contact-${contact.advisorId}`}
                            disabled={disabled}
                            checked={checked}
                            onCheckedChange={value =>
                              onToggleContact(contact.advisorId, value === true)
                            }
                            className="mt-0.5"
                          />
                          <span className="flex flex-col">
                            <span className="text-foreground text-base font-medium">
                              {contactDisplayName(contact)}
                            </span>
                            <span className="text-muted-foreground flex items-center gap-2 text-sm">
                              <span>{contact.advisorEmail}</span>
                              <span>-</span>
                              {contact.organizationName && <span>{contact.organizationName}</span>}
                            </span>
                          </span>
                        </Label>
                        {contact.isConnected && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            disabled={disabled}
                            aria-label={`Remove sharing with ${contactDisplayName(contact)}`}
                            onClick={() => onRequestDisconnect(contact)}
                            className="hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 />
                          </Button>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              <div className="flex justify-end">
                <Button
                  type="button"
                  onClick={onConnect}
                  disabled={disabled || isConnecting || !hasNewContactSelection}
                >
                  {isConnecting ? 'Connecting…' : 'Connect selected'}
                </Button>
              </div>
            </div>
          )}

          <Card className="gap-3 p-6 shadow-none">
            <p className="text-foreground text-sm font-medium">Don't see who you work with?</p>
            <p className="text-muted-foreground text-sm">
              Enter their name and email and we'll invite them to join Waterlily as your
              distribution partner.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row">
              {selectedPartners.length > 1 && (
                <select
                  value={effectiveOtherPartnerKey}
                  disabled={disabled}
                  onChange={event => setOtherPartnerKey(event.target.value)}
                  className="bg-background h-10 rounded-md border px-3 text-sm sm:max-w-[30%]"
                >
                  {selectedPartners.map(partner => (
                    <option
                      key={partner.partnerKey}
                      value={partner.partnerKey}
                    >
                      {partner.displayName}
                    </option>
                  ))}
                </select>
              )}
              <Input
                value={otherName}
                disabled={disabled}
                placeholder="Contact name"
                onChange={event => setOtherName(event.target.value)}
              />
              <Input
                type="email"
                value={otherEmail}
                disabled={disabled}
                placeholder="Contact email"
                onChange={event => setOtherEmail(event.target.value)}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  onRequestOther(effectiveOtherPartnerKey, otherName, otherEmail.trim());
                  setOtherName('');
                  setOtherEmail('');
                }}
                disabled={
                  disabled ||
                  isRequestingOther ||
                  !effectiveOtherPartnerKey ||
                  otherName.trim().length === 0 ||
                  !otherEmailValid
                }
              >
                {isRequestingOther ? 'Sending…' : 'Invite'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
